"use client";

const GOOGLE_ADS_ID = "AW-17773347749";
const LEAD_CONVERSION_LABEL =
	process.env.NEXT_PUBLIC_GOOGLE_ADS_LEAD_CONVERSION_LABEL;
const CHECKOUT_CONVERSION_LABEL =
	process.env.NEXT_PUBLIC_GOOGLE_ADS_CHECKOUT_CONVERSION_LABEL;

type GtagParams = Record<string, unknown>;
type ConsentValue = "granted" | "denied";

declare global {
	interface Window {
		gtag?: {
			(command: "event", eventName: string, eventParams?: GtagParams): void;
			(
				command: "consent",
				action: "default" | "update",
				params: Record<string, ConsentValue | number>,
			): void;
		};
	}
}

function removeEmptyValues(params: GtagParams): GtagParams {
	return Object.fromEntries(
		Object.entries(params).filter(([, value]) => value !== undefined),
	);
}

export function trackGoogleAdsEvent(
	eventName: string,
	params: GtagParams = {},
) {
	if (typeof window === "undefined" || typeof window.gtag !== "function") {
		return;
	}

	window.gtag("event", eventName, removeEmptyValues(params));
}

export function updateGoogleConsent(granted: boolean) {
	if (typeof window === "undefined" || typeof window.gtag !== "function") {
		return;
	}

	const value: ConsentValue = granted ? "granted" : "denied";
	window.gtag("consent", "update", {
		ad_storage: value,
		ad_user_data: value,
		ad_personalization: value,
		analytics_storage: value,
	});
}

export function trackGoogleAdsLeadConversion(formName: string) {
	trackGoogleAdsEvent("generate_lead", {
		event_category: "lead",
		event_label: formName,
		send_to: LEAD_CONVERSION_LABEL
			? `${GOOGLE_ADS_ID}/${LEAD_CONVERSION_LABEL}`
			: undefined,
	});
}

export function trackGoogleAdsCheckoutStart(params: {
	planId: number;
	planName: string;
	value: number;
	currency: "INR" | "USD";
}) {
	trackGoogleAdsEvent("begin_checkout", {
		value: params.value,
		currency: params.currency,
		items: [
			{
				item_id: String(params.planId),
				item_name: params.planName,
				price: params.value,
				quantity: 1,
			},
		],
		send_to: CHECKOUT_CONVERSION_LABEL
			? `${GOOGLE_ADS_ID}/${CHECKOUT_CONVERSION_LABEL}`
			: undefined,
	});
}

export function trackGoogleAdsPaymentInfo(params: {
	planId: number;
	planName: string;
	value: number;
	currency: "INR" | "USD";
	gateway: "payu" | "stripe";
	coupon?: string | null;
}) {
	trackGoogleAdsEvent("add_payment_info", {
		value: params.value,
		currency: params.currency,
		payment_type: params.gateway,
		coupon: params.coupon,
		items: [
			{
				item_id: String(params.planId),
				item_name: params.planName,
				price: params.value,
				quantity: 1,
			},
		],
	});
}
